import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

/**
 * Reusable list utilities: pagination + column sort + search filter.
 *
 * usePagedTable({ rows, defaultPageSize, defaultSort })
 * returns { pageRows, page, setPage, pageSize, setPageSize, totalPages,
 *          sortBy, sortDir, toggleSort, filters, setFilter, resetFilters,
 *          startIndex }
 */
export function usePagedTable({ rows, defaultPageSize = 25, defaultSort = null } = {}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortBy, setSortBy] = useState(defaultSort?.key || null);
  const [sortDir, setSortDir] = useState(defaultSort?.dir || 'asc');
  const [filters, setFilters] = useState({});

  const filtered = useMemo(() => {
    const activeKeys = Object.keys(filters).filter((k) => filters[k] && filters[k] !== 'all');
    if (activeKeys.length === 0) return rows;
    return rows.filter((row) => activeKeys.every((k) => {
      // Round 61 · Item 4 — special sentinel to filter for "field is missing"
      // (null, undefined, or empty string). Lets a dropdown offer a "Not set"
      // option without needing a separate memo per table.
      if (filters[k] === '__not_set__') {
        return row?.[k] == null || row?.[k] === '';
      }
      const v = String(row?.[k] ?? '').toLowerCase();
      return v === String(filters[k]).toLowerCase();
    }));
  }, [rows, filters]);

  const sorted = useMemo(() => {
    if (!sortBy) return filtered;
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = a?.[sortBy]; const bv = b?.[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      // Numeric sort when both look like numbers
      const an = Number(av); const bn = Number(bv);
      if (!Number.isNaN(an) && !Number.isNaN(bn) && av !== '' && bv !== '') {
        return sortDir === 'asc' ? an - bn : bn - an;
      }
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const pageRows = sorted.slice(startIndex, startIndex + pageSize);

  const toggleSort = (key) => {
    if (sortBy !== key) { setSortBy(key); setSortDir('asc'); }
    else if (sortDir === 'asc') setSortDir('desc');
    else { setSortBy(null); setSortDir('asc'); }
  };

  const setFilter = (key, value) => { setFilters((f) => ({ ...f, [key]: value })); setPage(1); };
  const resetFilters = () => { setFilters({}); setPage(1); };

  return {
    pageRows, page: safePage, setPage, pageSize, setPageSize, totalPages,
    sortBy, sortDir, toggleSort, filters, setFilter, resetFilters,
    startIndex, totalRows: sorted.length, unfilteredRows: rows.length,
    sorted, filtered,
  };
}

export const SortableTh = ({ children, sortKey, state, className = '' }) => {
  const active = state.sortBy === sortKey;
  const Icon = !active ? ChevronsUpDown : state.sortDir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <button
      type="button"
      onClick={() => state.toggleSort(sortKey)}
      className={`inline-flex items-center gap-1 font-medium text-left ${className}`}
      data-testid={`sort-${sortKey}`}
    >
      {children}
      <Icon className={`h-3 w-3 ${active ? 'text-blue-700' : 'text-slate-400'}`} />
    </button>
  );
};

export const PaginationBar = ({ state, testId = 'pagination' }) => {
  const { page, setPage, pageSize, setPageSize, totalPages, totalRows, startIndex } = state;
  const showing = totalRows === 0 ? '0' : `${startIndex + 1}–${Math.min(startIndex + pageSize, totalRows)}`;
  return (
    <div className="flex items-center justify-between gap-3 text-xs text-slate-600 pt-2" data-testid={testId}>
      <div className="flex items-center gap-2">
        <span>Rows per page:</span>
        <select
          className="border border-slate-200 rounded px-1.5 py-0.5 bg-white"
          value={pageSize}
          onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
          data-testid={`${testId}-page-size`}
        >
          {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <span className="text-slate-500">Showing {showing} of {totalRows}</span>
      </div>
      <div className="flex items-center gap-2">
        <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50" data-testid={`${testId}-prev`}>Prev</button>
        <span>Page {page} of {totalPages}</span>
        <button type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-slate-50" data-testid={`${testId}-next`}>Next</button>
      </div>
    </div>
  );
};
